import React, { useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  ArrowLeft,
  MessageCircle,
  Workflow,
  BarChart3,
  Users,
  Send, // Icono para el botón de CTA
  X, // Icono para cerrar el popup

  // --- Iconos añadidos para esta página ---
  Lock,             // Para Datos Atrapados
  FileWarning,      // Para Duplicidad
  // ArrowDown,     // Ya no se usa, eliminado
  DatabaseZap,      // Para el Hero (Silos)
  CheckCircle,       // Para el "Después" del gráfico

  // --- Iconos añadidos para el nuevo mapa de procesos ---
  ArrowRight,       // Para el flujo horizontal
  User,             // Para Vendedor
  Mail,             // Para Email (Pain point)
  UserCheck,        // Para Gerente
  UserCog,          // Para Finanzas
  DollarSign,       // Para Resultado
  AlertCircle as IconAlertCircle, // Renombrado para evitar conflicto
  XCircle,           // Para el flujo de rechazo
  TrendingUp,       // Para Ventajas
  TrendingDown      // Para Desventajas
} from 'lucide-react';

// import ContactForm from '../ContactForm';

import ParticlesBackground from '../components/ParticlesBackground'; // Asumiendo que ContactForm está en la ruta correcta
import LanguageSwitcher from '../components/LanguageSwitcher';

import ContactForm from '../ContactForm';


// --- COMPONENTES DE PÁGINA ---

// Header (Reutilizado)
const PageHeader = ({ onOpenPopup, t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; }}
        />
      </a>
      <div className="flex items-center gap-4">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {t('common.free_diagnostic')}
        </button>
      </div>
    </div>
  </header>
);


// --- PÁGINA DE DISPERSIÓN TECNOLÓGICA (SILOS) ---

const DispersionTecnologicaPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Estado para controlar el popup
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // --- INICIO: Lógica para "Click y Arrastrar" y Animación JS ---
  const [dragState, setDragState] = useState({
    isDragging: false,
    startX: 0,
    scrollLeft: 0,
    target: null, // Almacenará el ref del carrusel activo
  });

  const carouselAntesRef = useRef(null);
  const carouselDespuesRef = useRef(null);

  // Estados para pausar la animación JS al hacer hover
  const [isHoveringAntes, setIsHoveringAntes] = useState(false);
  const [isHoveringDespues, setIsHoveringDespues] = useState(false);

  const silosItems = useMemo(() => t('dispersion.silos.items', { returnObjects: true }), [t]);
  const beforeSteps = useMemo(() => t('dispersion.comparison.before.steps', { returnObjects: true }), [t]);
  const beforeDisadvantages = useMemo(
    () => t('dispersion.comparison.before.disadvantages.items', { returnObjects: true }),
    [t]
  );
  const afterSteps = useMemo(() => t('dispersion.comparison.after.steps', { returnObjects: true }), [t]);
  const normalizedAfterSteps = useMemo(() => {
    if (!Array.isArray(afterSteps)) return afterSteps;
    return {
      vendedor: afterSteps[0] || { actor: '', title: '' },
      sistema_notify: afterSteps[1] || { actor: '', title: '' },
      gerente_decision: afterSteps[2] || { actor: '', title: '' },
      si: {
        label: 'SÍ',
        sistema: afterSteps[3] || { actor: '', title: '' },
        resultado: afterSteps[4] || { actor: '', title: '' },
      },
      no: {
        label: 'NO',
        sistema: { actor: t('dispersion.comparison.after.steps.2.actor', { defaultValue: '' }), title: t('dispersion.comparison.after.steps.no.sistema.title', { defaultValue: 'Notifica Rechazo' }) },
        resultado: { actor: t('dispersion.comparison.after.steps.4.actor', { defaultValue: '' }), title: t('dispersion.comparison.after.steps.no.resultado.title', { defaultValue: 'Descuento Rechazado' }) },
      },
    };
  }, [afterSteps, t]);
  const afterAdvantages = useMemo(
    () => t('dispersion.comparison.after.advantages.items', { returnObjects: true }),
    [t]
  );
  const solutionItems = useMemo(() => t('dispersion.solution.items', { returnObjects: true }), [t]);

  const handleMouseDown = (e, ref) => {
    e.preventDefault();
    const carousel = ref.current;
    if (!carousel) return;
    setDragState({
      isDragging: true,
      startX: e.pageX - carousel.offsetLeft,
      scrollLeft: carousel.scrollLeft,
      target: ref,
    });
  };

  const handleMouseUpOrLeave = () => {
    // Solo cambia el estado de 'isDragging' si ya estaba 'true'
    setDragState(prev => {
      if (prev.isDragging) {
        return { ...prev, isDragging: false };
      }
      return prev;
    });
  };

  const handleMouseMove = (e) => {
    if (!dragState.isDragging || !dragState.target) return;
    e.preventDefault();
    const carousel = dragState.target.current;
    if (!carousel) return;
    
    const x = e.pageX - carousel.offsetLeft;
    const walk = (x - dragState.startX) * 2; // Multiplicador para un deslizamiento más rápido
    carousel.scrollLeft = dragState.scrollLeft - walk;
  };

  // --- INICIO: Nueva Lógica de Animación JS ---
  React.useEffect(() => {
    let animationFrameId;
    const speed = 1; // Velocidad de scroll (píxeles por frame)

    const animateCarousels = () => {
      // Animar "Antes"
      if (carouselAntesRef.current && !isHoveringAntes && !dragState.isDragging) {
        const el = carouselAntesRef.current;
        // Si llega al "pliegue" (la mitad), salta al inicio.
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = 0;
        } else {
          el.scrollLeft += speed;
        }
      }
      
      // Animar "Después"
      if (carouselDespuesRef.current && !isHoveringDespues && !dragState.isDragging) {
        const el = carouselDespuesRef.current;
        // Si llega al "pliegue" (la mitad), salta al inicio.
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = 0;
        } else {
          el.scrollLeft += speed;
        }
      }
      
      animationFrameId = requestAnimationFrame(animateCarousels);
    };

    // Iniciar la animación
    animationFrameId = requestAnimationFrame(animateCarousels);

    // Limpieza al desmontar
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isHoveringAntes, isHoveringDespues, dragState.isDragging]); // Dependencias
  // --- FIN: Nueva Lógica de Animación JS ---
  // --- FIN: Lógica para "Click y Arrastrar" ---


  // Función para abrir el popup
  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden'; // Evitar scroll en el fondo
  };

  // Función para cerrar el popup
  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto'; // Restaurar scroll
  };

  // Componente reutilizable para tarjetas de características (Modificado)
  const FeatureCard = ({ icon: Icon, title, children, colorClass, bgColorClass = "bg-gray-50", borderColorClass = "border-gray-200" }) => (
    <div className={`${bgColorClass} ${borderColorClass} border rounded-lg p-5 h-full transition hover:shadow-md`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className={colorClass} size={24} />
        <h4 className="font-semibold text-[#141414]">{title}</h4>
      </div>
      <div className="text-sm text-gray-700">{children}</div>
    </div>
  );


  // --- Helper Components for Process Map ---
  const ProcessMapStep = ({ icon: Icon, title, actor, colorClasses, isDecision = false }) => {
    // Componente para la forma de diamante (decisión)
    if (isDecision) {
      return (
        <div className="flex-shrink-0 flex items-center justify-center w-44 h-44">
          <div className={`transform rotate-45 w-36 h-36 border-2 rounded-2xl flex items-center justify-center ${colorClasses}`}>
            <div className="transform -rotate-45 text-center p-2">
              <Icon size={24} className="mb-2 mx-auto"/>
              <span className="text-xs font-semibold uppercase tracking-wide">{actor}</span>
              <span className="text-sm font-medium">{title}</span>
            </div>
          </div>
        </div>
      )
    }
    // Componente para el paso estándar (rectángulo)
    return (
      <div className={`flex-shrink-0 w-44 h-44 text-center flex flex-col items-center justify-center p-4 rounded-lg border-2 ${colorClasses}`}>
        <Icon size={24} className="mb-2"/>
        <span className="text-xs font-semibold uppercase tracking-wide">{actor}</span>
        <span className="text-sm font-medium">{title}</span>
      </div>
    );
  };

  // Flecha (siempre horizontal para el carrusel)
  const ProcessMapArrow = ({ label }) => (
    <div className="flex-shrink-0 flex flex-col items-center justify-center w-16 md:w-20 h-44">
      {label && <span className="text-xs font-bold text-gray-600 mb-1">{label}</span>}
      <ArrowRight size={24} className="text-gray-400" />
    </div>
  );
  
  // "Punto de dolor" (Pain point)
  const ProcessMapPainPoint = ({ text, icon: Icon, colorClasses }) => (
      <div className={`flex-shrink-0 flex flex-col items-center justify-center w-44 h-44 text-center p-4 rounded-lg border-2 ${colorClasses}`}>
          <Icon size={24} className="mb-2"/>
          <span className="text-sm font-semibold">{text}</span>
      </div>
  );
  // --- End Helper Components ---


  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <ParticlesBackground />
      {/* --- Estilos para la animación del carrusel (ELIMINADOS) --- */}
      {/* <style> ... </style> */}

      {/* Popup Modal para Bitrix24 */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label={t('common.close')}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('common.diagnostic_request')}</h3>
              <p className="text-gray-600 mb-6">{t('common.diagnostic_desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}
      
      <PageHeader onOpenPopup={openPopup} t={t} />

      {/* Contenido Principal */}
      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Botón Volver */}
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          {/* Hero de la Página */}
          <div className="text-center mb-16">
            <div className="inline-block bg-red-100 text-red-700 p-3 rounded-full mb-4">
              <DatabaseZap size={32} />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-4">
              {t('dispersion.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              {t('dispersion.hero.subtitle')}
            </p>
          </div>

          {/* --- SECCIÓN 1: EL PROBLEMA (ROJO) --- */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-4">{t('dispersion.silos.title')}</h2>
            <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12">
              {t('dispersion.silos.subtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <FeatureCard
                icon={Lock}
                colorClass="text-red-600"
                title={silosItems[0]?.title}
                bgColorClass="bg-white"
                borderColorClass="border-red-200"
              >
                <p>{silosItems[0]?.desc}</p>
              </FeatureCard>
              <FeatureCard
                icon={FileWarning}
                colorClass="text-red-600"
                title={silosItems[1]?.title}
                bgColorClass="bg-white"
                borderColorClass="border-red-200"
              >
                <p>{silosItems[1]?.desc}</p>
              </FeatureCard>
              <FeatureCard
                icon={BarChart3}
                colorClass="text-red-600"
                title={silosItems[2]?.title}
                bgColorClass="bg-white"
                borderColorClass="border-red-200"
              >
                <p>{silosItems[2]?.desc}</p>
              </FeatureCard>
              <FeatureCard
                icon={Users}
                colorClass="text-red-600"
                title={silosItems[3]?.title}
                bgColorClass="bg-white"
                borderColorClass="border-red-200"
              >
                <p>{silosItems[3]?.desc}</p>
              </FeatureCard>
            </div>
          </section>

          {/* --- SECCIÓN 3: DETALLE DE AUTOMATIZACIÓN (NUEVO DISEÑO CON CARRUSEL) --- */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-4">{t('dispersion.comparison.title')}</h2>
            <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12">
              {t('dispersion.comparison.subtitle')}
            </p>

            {/* Flujo "Antes" con Carrusel */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold text-red-700 mb-6 text-center">{t('dispersion.comparison.before.title')}</h3>
              {/* Contenedor del carrusel: AÑADIDO 'group', 'ref' y 'event handlers' */}
              <div 
                ref={carouselAntesRef}
                className="w-full overflow-hidden p-6 bg-white border-2 border-red-200 rounded-xl cursor-grab active:cursor-grabbing"
                onMouseDown={(e) => handleMouseDown(e, carouselAntesRef)}
                onMouseLeave={() => { handleMouseUpOrLeave(); setIsHoveringAntes(false); }}
                onMouseUp={handleMouseUpOrLeave}
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setIsHoveringAntes(true)}
              >
                {/* Pista del carrusel: Clases de animación CSS eliminadas */}
                <div className="flex w-max">
                  {/* --- Inicia Contenido Original --- */}
                  <div className="flex items-center">
                    <ProcessMapStep 
                      icon={User} 
                      title={beforeSteps[0]?.title}
                      actor={beforeSteps[0]?.actor}
                      colorClasses="border-red-300 bg-red-50 text-red-700" 
                    />
                    <ProcessMapArrow />
                    <ProcessMapPainPoint 
                      icon={Mail} 
                      text={beforeSteps[1]?.title}
                      colorClasses="border-dashed border-red-300 bg-white text-red-600"
                    />
                    <ProcessMapArrow />
                    <ProcessMapStep 
                      icon={UserCheck} 
                      title={beforeSteps[2]?.title}
                      actor={beforeSteps[2]?.actor}
                      colorClasses="border-red-300 bg-red-50 text-red-700" 
                    />
                    <ProcessMapArrow />
                    <ProcessMapPainPoint 
                      icon={FileWarning} 
                      text={beforeSteps[3]?.title}
                      colorClasses="border-dashed border-red-300 bg-white text-red-600"
                    />
                    <ProcessMapArrow />
                    <ProcessMapStep 
                      icon={UserCog} 
                      title={beforeSteps[4]?.title}
                      actor={beforeSteps[4]?.actor}
                      colorClasses="border-red-300 bg-red-50 text-red-700" 
                    />
                    <ProcessMapArrow />
                    <ProcessMapPainPoint 
                      icon={IconAlertCircle} 
                      text={beforeSteps[5]?.title}
                      colorClasses="border-red-600 bg-red-100 text-red-700 font-bold"
                    />
                    {/* Espaciador final REEMPLAZADO por separador visual */}
                    <div className="flex-shrink-0 flex items-center justify-center w-16 md:w-20 h-44">
                      <div className="w-px h-2/3 bg-gray-300"></div>
                    </div>
                  </div>
                  {/* --- Fin Contenido Original --- */}
                  
                  {/* --- Inicia Contenido Duplicado --- */}
                  <div className="flex items-center">
                    <ProcessMapStep 
                      icon={User} 
                      title={beforeSteps[0]?.title}
                      actor={beforeSteps[0]?.actor}
                      colorClasses="border-red-300 bg-red-50 text-red-700" 
                    />
                    <ProcessMapArrow />
                    <ProcessMapPainPoint 
                      icon={Mail} 
                      text={beforeSteps[1]?.title}
                      colorClasses="border-dashed border-red-300 bg-white text-red-600"
                    />
                    <ProcessMapArrow />
                    <ProcessMapStep 
                      icon={UserCheck} 
                      title={beforeSteps[2]?.title}
                      actor={beforeSteps[2]?.actor}
                      colorClasses="border-red-300 bg-red-50 text-red-700" 
                    />
                    <ProcessMapArrow />
                    <ProcessMapPainPoint 
                      icon={FileWarning} 
                      text={beforeSteps[3]?.title}
                      colorClasses="border-dashed border-red-300 bg-white text-red-600"
                    />
                    <ProcessMapArrow />
                    <ProcessMapStep 
                      icon={UserCog} 
                      title={beforeSteps[4]?.title}
                      actor={beforeSteps[4]?.actor}
                      colorClasses="border-red-300 bg-red-50 text-red-700" 
                    />
                    <ProcessMapArrow />
                    <ProcessMapPainPoint 
                      icon={IconAlertCircle} 
                      text={beforeSteps[5]?.title}
                      colorClasses="border-red-600 bg-red-100 text-red-700 font-bold"
                    />
                    {/* Espaciador final REEMPLAZADO por separador visual */}
                    <div className="flex-shrink-0 flex items-center justify-center w-16 md:w-20 h-44">
                      <div className="w-px h-2/3 bg-gray-300"></div>
                    </div>
                  </div>
                  {/* --- Fin Contenido Duplicado --- */}
                </div>
              </div>
              
              {/* --- INICIO: Sección de Ventajas/Desventajas (ANTES) --- */}
              {/* Contenedor ajustado a 1 columna */}
              <div className="mt-6 grid grid-cols-1 gap-4 text-sm">
                {/* Desventajas */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-5">
                  <h4 className="font-semibold text-red-700 flex items-center gap-2 mb-3">
                    <TrendingDown size={18} />
                    {t('dispersion.comparison.before.disadvantages.title')}
                  </h4>
                  {/* --- INICIO: Nuevo diseño de lista --- */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-2.5">
                      <XCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700"><strong>{beforeDisadvantages[0]?.label}:</strong> {beforeDisadvantages[0]?.desc}</p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <XCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700"><strong>{beforeDisadvantages[1]?.label}:</strong> {beforeDisadvantages[1]?.desc}</p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <XCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700"><strong>{beforeDisadvantages[2]?.label}:</strong> {beforeDisadvantages[2]?.desc}</p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <XCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700"><strong>{beforeDisadvantages[3]?.label}:</strong> {beforeDisadvantages[3]?.desc}</p>
                    </div>
                  </div>
                  {/* --- FIN: Nuevo diseño de lista --- */}
                </div>
                {/* Ventajas (ELIMINADAS) */}
              </div>
              {/* --- FIN: Sección de Ventajas/Desventajas (ANTES) --- */}

            </div>

            {/* Flujo "Después" con Carrusel y Condicional */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-green-700 mb-6 text-center">{t('dispersion.comparison.after.title')}</h3>
              {/* Contenedor del carrusel: AÑADIDO 'group', 'ref' y 'event handlers' */}
              <div 
                ref={carouselDespuesRef}
                className="w-full overflow-hidden p-6 bg-white border-2 border-green-200 rounded-xl cursor-grab active:cursor-grabbing"
                onMouseDown={(e) => handleMouseDown(e, carouselDespuesRef)}
                onMouseLeave={() => { handleMouseUpOrLeave(); setIsHoveringDespues(false); }}
                onMouseUp={handleMouseUpOrLeave}
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setIsHoveringDespues(true)}
              >
                {/* Pista del carrusel: Clases de animación CSS eliminadas */}
                <div className="flex w-max">
                  {/* --- Inicia Contenido Original --- */}
                  <div className="flex items-center">
                    <ProcessMapStep 
                      icon={User} 
                      title={normalizedAfterSteps?.vendedor?.title}
                      actor={normalizedAfterSteps?.vendedor?.actor}
                      colorClasses="border-green-300 bg-green-50 text-green-700" 
                    />
                    <ProcessMapArrow />
                    <ProcessMapStep 
                      icon={Workflow} 
                      title={normalizedAfterSteps?.sistema_notify?.title}
                      actor={normalizedAfterSteps?.sistema_notify?.actor}
                      colorClasses="border-green-300 bg-green-50 text-green-700" 
                    />
                    <ProcessMapArrow />
                    <ProcessMapStep 
                      icon={UserCheck} 
                      title={normalizedAfterSteps?.gerente_decision?.title}
                      actor={normalizedAfterSteps?.gerente_decision?.actor}
                      colorClasses="border-green-600 bg-green-100 text-green-800"
                      isDecision={true}
                    />

                    {/* Componente de Bifurcación (Branch) */}
                    <div className="flex-shrink-0 flex flex-col justify-center gap-4 py-4 ml-[-1.5rem] mr-[-1.5rem] md:ml-0 md:mr-0"> {/* Ajuste de solapamiento leve */}
                      {/* Rama SÍ (Arriba) */}
                      <div className="flex items-center">
                        <ProcessMapArrow label={normalizedAfterSteps?.si?.label} />
                        <ProcessMapStep 
                          icon={CheckCircle} 
                          title={normalizedAfterSteps?.si?.sistema?.title}
                          actor={normalizedAfterSteps?.si?.sistema?.actor}
                          colorClasses="border-green-300 bg-green-50 text-green-700" 
                        />
                        <ProcessMapArrow />
                        <ProcessMapStep 
                          icon={DollarSign} 
                          title={normalizedAfterSteps?.si?.resultado?.title}
                          actor={normalizedAfterSteps?.si?.resultado?.actor}
                          colorClasses="border-green-600 bg-green-100 text-green-700 font-bold"
                        />
                      </div>
                      {/* Rama NO (Abajo) */}
                      <div className="flex items-center">
                        <ProcessMapArrow label={normalizedAfterSteps?.no?.label} />
                        <ProcessMapStep 
                          icon={XCircle} 
                          title={normalizedAfterSteps?.no?.sistema?.title}
                          actor={normalizedAfterSteps?.no?.sistema?.actor}
                          colorClasses="border-red-300 bg-red-50 text-red-700" 
                        />
                        <ProcessMapArrow />
                        <ProcessMapStep 
                          icon={IconAlertCircle} 
                          title={normalizedAfterSteps?.no?.resultado?.title}
                          actor={normalizedAfterSteps?.no?.resultado?.actor}
                          colorClasses="border-red-600 bg-red-100 text-red-700 font-bold"
                        />
                      </div>
                    </div>
                    {/* Espaciador final REEMPLAZADO por separador visual */}
                    <div className="flex-shrink-0 flex items-center justify-center w-16 md:w-20 h-44">
                      <div className="w-px h-2/3 bg-gray-300"></div>
                    </div>
                  </div>
                  {/* --- Fin Contenido Original --- */}

                  {/* --- Inicia Contenido Duplicado --- */}
                  <div className="flex items-center">
                    <ProcessMapStep 
                      icon={User} 
                      title={normalizedAfterSteps?.vendedor?.title}
                      actor={normalizedAfterSteps?.vendedor?.actor}
                      colorClasses="border-green-300 bg-green-50 text-green-700" 
                    />
                    <ProcessMapArrow />
                    <ProcessMapStep 
                      icon={Workflow} 
                      title={normalizedAfterSteps?.sistema_notify?.title}
                      actor={normalizedAfterSteps?.sistema_notify?.actor}
                      colorClasses="border-green-300 bg-green-50 text-green-700" 
                    />
                    <ProcessMapArrow />
                    <ProcessMapStep 
                      icon={UserCheck} 
                      title={normalizedAfterSteps?.gerente_decision?.title}
                      actor={normalizedAfterSteps?.gerente_decision?.actor}
                      colorClasses="border-green-600 bg-green-100 text-green-800"
                      isDecision={true}
                    />
                    {/* Componente de Bifurcación (Branch) */}
                    <div className="flex-shrink-0 flex flex-col justify-center gap-4 py-4 ml-[-1.5rem] mr-[-1.5rem] md:ml-0 md:mr-0">
                      {/* Rama SÍ (Arriba) */}
                      <div className="flex items-center">
                        <ProcessMapArrow label={normalizedAfterSteps?.si?.label} />
                        <ProcessMapStep 
                          icon={CheckCircle} 
                          title={normalizedAfterSteps?.si?.sistema?.title}
                          actor={normalizedAfterSteps?.si?.sistema?.actor}
                          colorClasses="border-green-300 bg-green-50 text-green-700" 
                        />
                        <ProcessMapArrow />
                        <ProcessMapStep 
                          icon={DollarSign} 
                          title={normalizedAfterSteps?.si?.resultado?.title}
                          actor={normalizedAfterSteps?.si?.resultado?.actor}
                          colorClasses="border-green-600 bg-green-100 text-green-700 font-bold"
                        />
                      </div>
                      {/* Rama NO (Abajo) */}
                      <div className="flex items-center">
                        <ProcessMapArrow label={normalizedAfterSteps?.no?.label} />
                        <ProcessMapStep 
                          icon={XCircle} 
                          title={normalizedAfterSteps?.no?.sistema?.title}
                          actor={normalizedAfterSteps?.no?.sistema?.actor}
                          colorClasses="border-red-300 bg-red-50 text-red-700" 
                        />
                        <ProcessMapArrow />
                        <ProcessMapStep 
                          icon={IconAlertCircle} 
                          title={normalizedAfterSteps?.no?.resultado?.title}
                          actor={normalizedAfterSteps?.no?.resultado?.actor}
                          colorClasses="border-red-600 bg-red-100 text-red-700 font-bold"
                        />
                      </div>
                    </div>
                     {/* Espaciador final REEMPLAZADO por separador visual */}
                    <div className="flex-shrink-0 flex items-center justify-center w-16 md:w-20 h-44">
                      <div className="w-px h-2/3 bg-gray-300"></div>
                    </div>
                  </div>
                  {/* --- Fin Contenido Duplicado --- */}
                </div>
              </div>

              {/* --- INICIO: Sección de Ventajas/Desventajas (DESPUÉS) --- */}
              {/* Contenedor ajustado a 1 columna */}
              <div className="mt-6 grid grid-cols-1 gap-4 text-sm">
                {/* Ventajas */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-5">
                  <h4 className="font-semibold text-green-700 flex items-center gap-2 mb-3">
                    <TrendingUp size={18} />
                    {t('dispersion.comparison.after.advantages.title')}
                  </h4>
                  {/* --- INICIO: Nuevo diseño de lista --- */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-2.5">
                      <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700"><strong>{afterAdvantages[0]?.label}:</strong> {afterAdvantages[0]?.desc}</p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700"><strong>{afterAdvantages[1]?.label}:</strong> {afterAdvantages[1]?.desc}</p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700"><strong>{afterAdvantages[2]?.label}:</strong> {afterAdvantages[2]?.desc}</p>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700"><strong>{afterAdvantages[3]?.label}:</strong> {afterAdvantages[3]?.desc}</p>
                    </div>
                  </div>
                  {/* --- FIN: Nuevo diseño de lista --- */}
                </div>
                {/* Desventajas (ELIMINADAS) */}
              </div>
              {/* --- FIN: Sección de Ventajas/Desventajas (DESPUÉS) --- */}

            </div>
          </section>


          {/* --- INICIO: SECCIÓN 4 (NUEVA): LA SOLUCIÓN (AZUL) --- */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-4">{t('dispersion.solution.title')}</h2>
            <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12">
              {t('dispersion.solution.subtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FeatureCard
                icon={DatabaseZap}
                colorClass="text-blue-600"
                title={solutionItems[0]?.title}
                bgColorClass="bg-blue-50"
                borderColorClass="border-blue-200"
              >
                <p>{solutionItems[0]?.desc}</p>
              </FeatureCard>
              <FeatureCard
                icon={Workflow}
                colorClass="text-blue-600"
                title={solutionItems[1]?.title}
                bgColorClass="bg-blue-50"
                borderColorClass="border-blue-200"
              >
                <p>{solutionItems[1]?.desc}</p>
              </FeatureCard>
              <FeatureCard
                icon={MessageCircle}
                colorClass="text-blue-600"
                title={solutionItems[2]?.title}
                bgColorClass="bg-blue-50"
                borderColorClass="border-blue-200"
              >
                <p>{solutionItems[2]?.desc}</p>
              </FeatureCard>
              <FeatureCard
                icon={BarChart3}
                colorClass="text-blue-600"
                title={solutionItems[3]?.title}
                bgColorClass="bg-blue-50"
                borderColorClass="border-blue-200"
              >
                <p>{solutionItems[3]?.desc}</p>
              </FeatureCard>
            </div>
          </section>
          {/* --- FIN: SECCIÓN 4 (NUEVA) --- */}


        </div>

        {/* --- SECCIÓN CTA (REUTILIZADA Y MODIFICADA) --- */}
        <section className="max-w-4xl mx-auto mt-24 mb-12">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold text-[#141414] mb-4">
              {t('dispersion.final_cta.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto mb-8">
              {t('dispersion.final_cta.desc')}
            </p>
            {/* Botón para abrir el popup */}
            <button
              onClick={openPopup}
              className="inline-flex items-center justify-center gap-2 bg-[#141414] text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition font-semibold"
            >
              <Send size={18} />
              {t('dispersion.final_cta.btn')}
            </button>
          </div>
        </section>

      </main>
    </div>
  );
};

export default DispersionTecnologicaPage;

